import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Sun, Globe, Phone } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      // Close mobile menu on scroll
      if (isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Handle scroll to anchor on page load
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleNavClick = (e, link) => {
    // Always close menu on mobile
    setIsOpen(false);
    
    // Check if the link contains a hash (anchor link)
    if (link.to.includes('#')) {
      e.preventDefault();
      
      const hash = link.to.split('#')[1]; // Get the part after #
      const path = link.to.split('#')[0] || '/'; // Get the part before # (default to '/')
      
      // Function to scroll to element
      const scrollToElement = () => {
        const element = document.getElementById(hash);
        if (element) {
          const navbarHeight = 80; // Height of fixed navbar
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      };
      
      // If we're already on the target page, just scroll
      if (location.pathname === path) {
        scrollToElement();
      } else {
        // Navigate to the page first, then scroll
        navigate(path);
        // Wait for navigation and then scroll
        setTimeout(scrollToElement, 100);
      }
    } else {
      // For normal links (no hash), scroll to top after navigation
      // Navigate first
      navigate(link.to);
      // Then scroll to top after a short delay
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/sechoir-solaire', label: 'Séchoir solaire' },
    { to: '/batiment-sechoir-offert', label: 'Bâtiment offert' },
    { to: '/panneau-pvt-air', label: 'Panneau PVT air' },
    { to: '/installateurs', label: 'Installateurs' },
    { to: '/bureau-etudes', label: "Bureau d'Études" },
    { to: '/chauffage', label: 'Chauffage' },
    { to: '/financement', label: 'Financement' },
    { to: '/fiche-cee-agri-eq-110', label: 'Fiche CEE' },
    { to: '/realisations', label: 'Références' },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => {
    if (path.includes('#')) {
      const [pathname] = path.split('#');
      return location.pathname === (pathname || '/');
    }
    return location.pathname === path;
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
        scrolled || isOpen ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 relative z-50" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
              <Sun className="w-6 h-6 text-white" />
            </div>
            <span className="hidden sm:inline text-xl md:text-2xl font-bold font-['Manrope'] text-[#F97316]" style={{ whiteSpace: 'nowrap' }}>
              Thermasol&nbsp;Air&nbsp;Systems<sup>®</sup>
            </span>
            <span className="sm:hidden text-lg font-bold font-['Manrope'] text-[#F97316]" style={{ whiteSpace: 'nowrap' }}>
              Thermasol<sup>®</sup>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => handleNavClick(e, link)}
                data-testid={`nav-link-${link.to.replace('/', '').replace('#', '') || 'home'}`}
                className={`font-medium transition-colors ${
                  isActive(link.to)
                    ? 'text-[#F97316]'
                    : 'text-[#0B1120] hover:text-[#F97316]'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Phone Number */}
            <a
              href="tel:0184190449"
              className="flex items-center gap-2 px-4 py-2 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] transition-colors font-medium"
            >
              <Phone className="w-4 h-4" />
              <span className="whitespace-nowrap">01 84 19 04 49</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 relative z-50"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6 text-[#0B1120]" /> : <Menu className="w-6 h-6 text-[#0B1120]" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="lg:hidden absolute top-20 left-0 right-0 bg-white shadow-lg border-t border-gray-200 animate-fade-in" 
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={(e) => handleNavClick(e, link)}
                    className={`font-medium py-3 px-4 rounded-lg transition-colors ${
                      isActive(link.to) 
                        ? 'bg-[#F97316]/10 text-[#F97316]' 
                        : 'text-[#0B1120] hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {/* Phone Number Mobile */}
                <a 
                  href="tel:0184190449" 
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span>01 84 19 04 49</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
